import { Card, CardHeader, Input, Typography } from "@/components/ui";
import { FilterBar } from "@/components/ui/FilterBar/FilterBar";
import type { FilterOption } from "@/components/ui/FilterSelector/FilterSelector";
import { getUsersOptions } from "@/lib/data/react-query";
import type { ContactEventLog } from "@/lib/types";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime, formatUsersName } from "@/lib/utils/format";
import { ContactEventLogCard } from "./ContactEventLogCard";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import Fuse from "fuse.js";
import { useCallback, useMemo, useState } from "react";

export const ContactEventLogs = ({
  events,
}: {
  events?: ContactEventLog[];
}) => {
  const eventLogData = useMemo(() => events ?? [], [events]);
  const [query, setQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<
    Partial<Record<string, string[]>>
  >({});
  const { data: users } = useQuery(getUsersOptions());
  const getUserForUserId = useCallback(
    (id: string) => {
      const user = users?.data.find((user) => user.id === id);
      const userName = user
        ? `${user.firstName} ${user.lastName}`
        : "Unknown User";
      return { userName, user };
    },
    [users],
  );

  const sortedEvents = useMemo(() => {
    if (!eventLogData.length) {
      return [];
    }
    return [...eventLogData].sort((a, b) => {
      const firstDate = new Date(b.dateCreated).getTime();
      const secondDate = new Date(a.dateCreated).getTime();
      return firstDate - secondDate;
    });
  }, [eventLogData]);

  const handleAddFilter = useCallback((key: string, value: string) => {
    if (!value) {
      return;
    }

    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const isSingleValueKey = key === "afterDate" || key === "beforeDate";

      if (!isSingleValueKey && existingValues.includes(value)) {
        return prev;
      }

      return {
        ...prev,
        [key]: isSingleValueKey ? [value] : [...existingValues, value],
      };
    });
  }, []);

  const handleRemoveFilter = useCallback((key: string, value: string) => {
    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const nextValues = existingValues.filter((current) => current !== value);

      if (nextValues.length === 0) {
        const entries = Object.entries(prev).filter(
          ([filterKey]) => filterKey !== key,
        );
        return Object.fromEntries(entries) as Partial<Record<string, string[]>>;
      }

      return {
        ...prev,
        [key]: nextValues,
      };
    });
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.values(selectedFilters).some(
      (values) => Array.isArray(values) && values.length > 0,
    );
  }, [selectedFilters]);

  const userFilterItems = useMemo(() => {
    const actors = new Map<string, string>();
    sortedEvents.forEach((event) => {
      const actorId = event.actor?.id ?? event.actorUserId;
      if (actorId) {
        actors.set(
          actorId,
          formatUsersName(event.actor) ?? getUserForUserId(actorId).userName,
        );
      }
    });

    return Array.from(actors, ([value, label]) => ({ label, value })).sort(
      (a, b) => a.label.localeCompare(b.label),
    );
  }, [getUserForUserId, sortedEvents]);

  const actionTypeItems = useMemo(() => {
    const types = new Set<string>();
    sortedEvents.forEach((event) => {
      if (event.actionType) {
        types.add(event.actionType);
      }
    });

    return Array.from(types)
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({
        label: type,
        value: type,
      }));
  }, [sortedEvents]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [];

    if (userFilterItems.length) {
      options.push({
        label: "Actor",
        value: "id",
        input: {
          type: "select",
          placeholder: "Select actor",
          items: userFilterItems,
        },
      });
    }

    if (actionTypeItems.length) {
      options.push({
        label: "Action",
        value: "actionType",
        input: {
          type: "select",
          placeholder: "Select action",
          items: actionTypeItems,
        },
      });
    }

    options.push({
      label: "Date",
      value: "date",
      input: {
        type: "date",
        placeholder: "Select date",
        conditionOptions: [
          { label: "Before", value: "beforeDate" },
          { label: "After", value: "afterDate" },
        ],
      },
    });

    return options;
  }, [actionTypeItems, userFilterItems]);

  const filteredBySelectorAndDate = useMemo(() => {
    const { id: userIds = [], actionType: actionTypes = [] } = selectedFilters;
    const afterDateValue = (selectedFilters.afterDate ?? [])[0];
    const beforeDateValue = (selectedFilters.beforeDate ?? [])[0];
    const afterDateBoundary = afterDateValue
      ? dayjs(afterDateValue, "YYYY-MM-DD", true).startOf("day")
      : null;
    const beforeDateBoundary = beforeDateValue
      ? dayjs(beforeDateValue, "YYYY-MM-DD", true).endOf("day")
      : null;

    return sortedEvents.filter((event) => {
      const matchesUser =
        !userIds.length ||
        userIds.includes(event.actor?.id ?? event.actorUserId ?? "");
      const matchesActionType =
        !actionTypes.length || actionTypes.includes(event.actionType ?? "");

      if (!matchesUser || !matchesActionType) {
        return false;
      }

      const eventDate = dayjs(
        convertToLocalFromDatabaseDateTime(event.dateCreated) ??
          event.dateCreated,
      );

      if (!eventDate.isValid()) {
        return true;
      }

      if (afterDateBoundary && eventDate.isBefore(afterDateBoundary)) {
        return false;
      }

      if (beforeDateBoundary && eventDate.isAfter(beforeDateBoundary)) {
        return false;
      }

      return true;
    });
  }, [selectedFilters, sortedEvents]);

  const filteredEvents = useMemo(() => {
    const sanitizedQuery = query.trim();

    if (!sanitizedQuery) return filteredBySelectorAndDate;

    const searchable = filteredBySelectorAndDate.map((event) => ({
      ...event,
      author:
        formatUsersName(event.actor) ??
        formatUsersName(getUserForUserId(event.actorUserId ?? "").user) ??
        "",
      dateCreatedString: formatDateTime(
        convertToLocalFromDatabaseDateTime(event.dateCreated) ?? "",
      ),
    }));

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "message", weight: 0.8 },
        { name: "actionType", weight: 0.2 },
        { name: "author", weight: 0.2 },
        { name: "dateCreatedString", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
      useExtendedSearch: false,
    });

    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [filteredBySelectorAndDate, getUserForUserId, query]);

  return (
    <div className="bg-accent flex h-full min-h-0 flex-col gap-2 overflow-hidden rounded-md p-4">
      <div className="mx-1 flex flex-shrink-0 flex-col gap-2">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col">
            <FilterBar
              filterOptions={filterOptions}
              selectedFilters={selectedFilters}
              onAddFilter={handleAddFilter}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-full max-w-[280px] sm:max-w-[320px]">
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder="Search events"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex-1 space-y-3 overflow-y-auto pr-2 pb-4">
        {filteredEvents.length === 0 ? (
          <Card className="w-full border-2 border-dashed">
            <CardHeader className="space-y-1">
              <Typography className="text-center">No events found</Typography>
              <Typography variant="subHeading" className="text-center">
                {query || hasActiveFilters
                  ? "Try adjusting your search term"
                  : "Events will appear here as they are added to the contact"}
              </Typography>
            </CardHeader>
          </Card>
        ) : (
          filteredEvents.map((event) => (
            <ContactEventLogCard key={event.id} event={event} />
          ))
        )}
      </div>
    </div>
  );
};
